import { CalendarDateService, L10NShape } from '@uplift/services';
import { VALIDATION_HOME_PHONE, VALIDATION_MOBILE_PHONE, VALIDATION_WORK_PHONE } from '@uplift/validations';
import { ObjectSchema, boolean, object, string } from 'yup';
import { createDobFieldValidation, useWithAddressVerify, useAlert } from '@uplift/ui-v2';
import { ENGLISH_MATCHES, ENGLISH_MATCHES_WITH_SPACE, NUMBERS_MATCHES, ROUTING_CONFIG } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { patchUserProfile, rewriteUserContacts, setProviderRegular } from '@uplift/api';
import { useHistory } from 'react-router';
import { isAxiosError } from 'axios';
import { getFormErrorsFromRewriteUserContactsErrorResponse } from '@uplift/utils';
import { useCurrentuserRefetch } from '@uplift/contexts';
import { useMemo } from 'react';
import { ContactName } from '@uplift/types';

export type AddressFormValues = {
  predefined?: boolean;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
};

export type AboutYouForm = {
  preferredName: string;
  dateOfBirth: string;
  mobilePhone: string;
  workPhone: string;
  homePhone?: string;
  address: AddressFormValues;
};

export const addressFormValidation = (l10n: L10NShape) => object({
  predefined: boolean().notRequired().default(false),
  address1: string()
    .matches(ENGLISH_MATCHES, l10n.validations.couldContainEnglish)
    .max(50, l10n.validations.client.address.line1TooLong)
    .required('Enter your street address'),
  address2: string()
    .nullable()
    .notRequired()
    .matches(ENGLISH_MATCHES, { message: l10n.validations.couldContainEnglish, excludeEmptyString: true })
    .max(50, l10n.validations.client.address.line1TooLong),
  city: string()
    .matches(ENGLISH_MATCHES_WITH_SPACE, l10n.validations.couldContainEnglishWithSpace)
    .max(50, l10n.validations.client.address.cityTooLong)
    .required('Enter your city'),
  state: string()
    .nullable()
    .required('Select your state'),
  zip: string()
    .matches(NUMBERS_MATCHES, l10n.validations.digitsOnly)
    .min(5, l10n.validations.client.address.zipEmpty)
    .max(5, l10n.validations.client.address.zipEmpty)
    .required('Enter your home zip code'),
});

export const isAddressIncomplete = (address: AddressFormValues, l10n: L10NShape): boolean => {
  if (address.predefined) return false;
  return !addressFormValidation(l10n).isValidSync(address);
};

export const useAboutYouFormik = (userId: string, nextRoute = ROUTING_CONFIG.main, { skipSetRegular = false } = {}) => {
  const { push } = useHistory();
  const l10n = useL10n();
  const snackbar = useAlert();
  const withAddressVerify = useWithAddressVerify();
  const { refetchUser } = useCurrentuserRefetch();

  const aboutYouValidation: ObjectSchema<AboutYouForm> = useMemo(() => object({
    preferredName: string()
      .matches(ENGLISH_MATCHES, l10n.validations.couldContainEnglish)
      .max(64, l10n.validations.lengthMatches(1, 64)),
    dateOfBirth: createDobFieldValidation(l10n, { requiredMsg: 'Enter your date of birth' })
      .test({
        name: 'be-over-18',
        test: (value) => {
          const date = CalendarDateService.date(value);
          const mustBe18 = CalendarDateService.date().subtract(18, 'year');

          return date.isBefore(mustBe18);
        },
        message: 'You must be at least 18 to use UpLift yourself'
      }),
    mobilePhone: VALIDATION_MOBILE_PHONE,
    workPhone: VALIDATION_WORK_PHONE,
    homePhone: VALIDATION_HOME_PHONE,
    address: addressFormValidation(l10n),
  }) as ObjectSchema<AboutYouForm>, [ l10n ]);

  const initialValues: AboutYouForm = useMemo(() => ({
    preferredName: '',
    dateOfBirth: '',
    mobilePhone: '',
    workPhone: '',
    address: {
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
    }
  }), []);

  const handleSubmit = withAddressVerify(async (values: AboutYouForm, { setErrors }) => {
    try {
      await patchUserProfile(userId, {
        preferredName: values.preferredName ? values.preferredName : undefined,
        dateOfBirth: values.dateOfBirth,
        address: {
          line1: values.address.address1,
          line2: values.address.address2 ?? '',
          city: values.address.city,
          stateId: values.address.state,
          zip: values.address.zip
        }
      });
    } catch (err) {
      console.error(err);
      snackbar.error('Unable to save your information');

      return;
    }

    try {
      await rewriteUserContacts(userId, {
        phones: {
          [ContactName.mobile]: values.mobilePhone,
          [ContactName.work]: values.workPhone,
          [ContactName.home]: values.homePhone ? values.homePhone : undefined
        }
      });

    } catch (err) {
      if (isAxiosError(err)) {
        const contactsErrors = getFormErrorsFromRewriteUserContactsErrorResponse(err);

        if (contactsErrors) {
          setErrors({
            mobilePhone: contactsErrors.cell,
            workPhone: contactsErrors.work,
            homePhone: contactsErrors.home
          });
        }

        return;
      }

      snackbar.error('Unable to save your data');
    }

    if (!skipSetRegular) {
      await setProviderRegular({ id: userId });
      refetchUser();
    }

    push(nextRoute);
  }, {
    shouldCheck: true,
    initialAddress: initialValues.address,
    getAddressFromFormikValues: ({ address }) => ({
      address1: address.address1,
      address2: address.address2,
      city: address.city,
      state: address.state,
      zip: address.zip,
      predefined: address.predefined
    })
  });

  return {
    aboutYouValidation,
    initialValues,
    handleSubmit
  };
};
